import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const PlatformPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
        email: '',
        type: 'merchant',
        ageConfirm: false
      });
    const [isSubmitting, setIsSubmitting] = useState(false);
      const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Platform Architecture — 7-Layer Smart Contracts | Rewardly';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '38+ smart contracts across 7 layers on Polygon. AI receipt validation. 5-path redemption. Patent-pending earn-time commitment architecture.');
    setMeta('og:title', 'Rewardly Protocol — 7-Layer Smart Contract Architecture');
    setMeta('og:description', '38+ smart contracts on Polygon. AI validation. Earn-time commitment. Built for security, scale, and trust.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/platform');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Rewardly Protocol — 7-Layer Smart Contract Architecture');
    setMeta('twitter:description', '38+ smart contracts on Polygon. AI validation. Earn-time commitment. Built for security, scale, and trust.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/platform');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const vpRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dcRef = useRef<HTMLDivElement>(null);
  const dcBRef = useRef<HTMLDivElement>(null);
  const dcTRef = useRef<HTMLDivElement>(null);
  const dcDRef = useRef<HTMLDivElement>(null);
  const dcCRef = useRef<HTMLDivElement>(null);
  const dcPRef = useRef<HTMLDivElement>(null);
  const dcXRef = useRef<HTMLButtonElement>(null);
  const flsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vp = vpRef.current!; const cv = canvasRef.current!;
    if (!vp || !cv) return;
    const cx = cv.getContext('2d')!;
    if (!cx) return;

    const LAYERS = [
      {id:1,nm:'Foundation',col:'#2563EB',c:['AccessManager','BaseModuleStorage','ProtocolConfig','RewardlyProxyManager'],l:['RewardlyConstants','RewardlyMath','RewardlyUtils','StorageCoreLib','RegionalOpsLib','EmergencyControlLib'],desc:'Role-based access control with 12 active roles, shared storage vault, 93 runtime-configurable parameters, proxy governance with emergency delays.',deps:'None — bedrock layer',used:'Every contract in L2–L7'},
      {id:2,nm:'Control + Policy',col:'#7C3AED',c:['ControlManager','PriceOracle','TokenomicsManager'],l:['EmergencyLib','SecurityComplianceLib','RegionalLib'],desc:'Unified 3-stage validation cascade. Dual-oracle pricing (Chainlink + TWAP) with 3-tier circuit breakers. 120B supply policy engine.',deps:'Layer 1',used:'Every contract in L3–L7'},
      {id:3,nm:'Money Core',col:'#DB2777',c:['RewardlyToken','FeeManager','TreasuryManager','EconomicsManager'],l:['TreasuryMathLib','TreasuryAllocationLib','TreasuryValidationLib'],desc:'RWLY ERC-20 token (120B cap, fee-on-transfer). 6-bucket fee distribution. 4 treasury pools with 2% epoch caps and pool health factors.',deps:'Layer 1 + 2',used:'L4, L5, L6, L7'},
      {id:4,nm:'Operations',col:'#EA580C',c:['EscrowManager','ConversionRouter','RegionalSettlementManager','ReserveRebalancer','BridgeOrchestrator','RWLYOFTAdapter','RWLYNttAdapter','wRWLY'],l:[],desc:'5-path conversion with USDC settlement. Coalition escrow with health factors. Dual-adapter bridge (LayerZero V2 + Wormhole NTT).',deps:'Layer 1 + 2 + 3',used:'L5, L6, L7'},
      {id:5,nm:'Distribution',col:'#059669',c:['StakingManager','MiningManager','VestingManager'],l:['StakingCalculationLib','VestingCoreLib','VestingCalculationLib'],desc:'veRWLY staking with 3-layer yield. 12-program Proof-of-Loyalty mining with √score. Token vesting across 8 categories.',deps:'Layer 1 + 2 + 3',used:'L6, L7'},
      {id:6,nm:'Business Registry',col:'#D4A853',c:['CoalitionRegistry','EnterpriseRegistry','MerchantRegistry'],l:[],desc:'On-chain identity and membership. Coalition governance (anchor or consortium). Enterprise KYC. Merchant tier management.',deps:'Layer 1 + 2',used:'L3 (fee tiers), L4 (escrow)'},
      {id:7,nm:'Market Operations',col:'#0EA5E9',c:['InventoryManager','QuickSwapV3Executor','UniswapV4Executor','OdosAggregatorExecutor','RewardlyHook'],l:[],desc:'Internal RWLY/USDC crossing engine. Batch fulfillment. Net settlement via hot-swappable executor every 4h (Gelato). Auto-replenish from Treasury.',deps:'Layer 1 + 2 + 3 + 4',used:'DEX / End users'}
    ];
    const FLOWS = [
      {nm:'Enterprise Onboards',ic:'↓',col:'#2563EB',layers:[1,2,6,4,3]},
      {nm:'Points → RWLY',ic:'⇄',col:'#059669',layers:[1,2,4,7,3]},
      {nm:'Proof-of-Loyalty Mining',ic:'⛏',col:'#D4A853',layers:[1,2,5,3]},
      {nm:'Cross-Chain Bridge',ic:'🌐',col:'#8B5CF6',layers:[1,2,4,3]},
      {nm:'Pool Health Gating',ic:'♥',col:'#F43F5E',layers:[1,2,3,4,7]}
    ];

    let W: number, H: number, dpr: number;
    function resize() {
      const r = vp.getBoundingClientRect(); dpr = Math.min(devicePixelRatio, 2);
      W = r.width; H = r.height; cv.width = W * dpr; cv.height = H * dpr; cx.setTransform(dpr,0,0,dpr,0,0);
    }
    resize();

    let cT=0.55,cP=0.7,cD=480,tT=0.55,tP=0.7,tD=480;
    let auto=true,sel=-1,hl: number[]|null=null;

    function proj(x:number,y:number,z:number){
      const ct=Math.cos(cT),st=Math.sin(cT),cp=Math.cos(cP),sp=Math.sin(cP);
      const rx=x*ct-z*st,rz=x*st+z*ct,ry=y*cp-rz*sp,rz2=y*sp+rz*cp;
      const d=Math.max(80,cD+rz2),s=cD/d;
      return{x:W/2+rx*s,y:H/2-ry*s,s,ok:(cD+rz2)>30,z:rz2};
    }
    const SW=240,SD=150,SH=10,GAP=50,BY=-(LAYERS.length-1)*GAP/2;
    const ap=Array.from({length:50},()=>({x:(Math.random()-.5)*500,y:(Math.random()-.5)*400,z:(Math.random()-.5)*500,vy:.04+Math.random()*.1,a:.05+Math.random()*.1,r:.4+Math.random()*.7}));
    const cp2: {a:number,b:number,t:number,s:number,xo:number,zo:number}[]=[];
    [[0,2],[1,3],[2,4],[3,6],[5,2],[2,6]].forEach(([a,b])=>{for(let i=0;i<2;i++)cp2.push({a,b,t:Math.random(),s:.001+Math.random()*.002,xo:(Math.random()-.5)*30,zo:(Math.random()-.5)*18});});

    function rgba(h:string,a:number){return`rgba(${parseInt(h.slice(1,3),16)},${parseInt(h.slice(3,5),16)},${parseInt(h.slice(5,7),16)},${a})`}
    function rr(x:number,y:number,w:number,h:number,r:number){cx.beginPath();cx.moveTo(x+r,y);cx.lineTo(x+w-r,y);cx.quadraticCurveTo(x+w,y,x+w,y+r);cx.lineTo(x+w,y+h-r);cx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);cx.lineTo(x+r,y+h);cx.quadraticCurveTo(x,y+h,x,y+h-r);cx.lineTo(x,y+r);cx.quadraticCurveTo(x,y,x+r,y);cx.closePath();}
    function quad(p:{x:number,y:number}[],idx:number[],f:string|null,s:string|null,lw?:number){cx.beginPath();cx.moveTo(p[idx[0]].x,p[idx[0]].y);for(let j=1;j<idx.length;j++)cx.lineTo(p[idx[j]].x,p[idx[j]].y);cx.closePath();if(f){cx.fillStyle=f;cx.fill();}if(s){cx.strokeStyle=s;cx.lineWidth=lw||.6;cx.stroke();}}
    function isHL(i:number){return hl?hl.includes(i+1):false;}
    function alph(i:number){if(!hl)return sel===i?.3:.1;return isHL(i)?.35:.025;}

    let hits:{i:number,x1:number,x2:number,y1:number,y2:number,z:number}[]=[];
    function slab(i:number,t:number){
      const L=LAYERS[i],on=sel===i,hi=isHL(i),dim=!!hl&&!hi;
      const y=BY+i*GAP+Math.sin(t*.5+i*.4)*1.5;
      const hw=SW/2,hd=SD/2,hh=SH/2;
      const v=[{x:-hw,y:y+hh,z:-hd},{x:hw,y:y+hh,z:-hd},{x:hw,y:y+hh,z:hd},{x:-hw,y:y+hh,z:hd},{x:-hw,y:y-hh,z:-hd},{x:hw,y:y-hh,z:-hd},{x:hw,y:y-hh,z:hd},{x:-hw,y:y-hh,z:hd}];
      const p=v.map(vv=>proj(vv.x,vv.y,vv.z));
      const a=alph(i),ea=(on||hi)?.6:dim?.05:.22,col=L.col,ec=rgba(col,ea);
      quad(p,[3,2,6,7],rgba(col,a*.5),ec);quad(p,[3,0,4,7],rgba(col,a*.45),ec);quad(p,[2,1,5,6],rgba(col,a*.45),ec);quad(p,[4,5,6,7],rgba(col,a*.3),ec);quad(p,[0,1,5,4],rgba(col,a*.6),ec);quad(p,[0,1,2,3],rgba(col,a),ec,(on||hi)?1.5:.6);
      if(hi){cx.save();cx.shadowColor=col;cx.shadowBlur=12;quad(p,[0,1,2,3],null,rgba(col,.5),1.2);cx.restore();}
      const lp=proj(0,y+hh+2,0);
      if(lp.ok){const txt=`L${L.id}  ${L.nm}`,fs=Math.max(9.5,11*lp.s);cx.font=`700 ${fs}px Inter,sans-serif`;const tw=cx.measureText(txt).width,bw=tw+14,bh=fs+6;cx.save();cx.globalAlpha=dim?.2:(on||hi)?.95:.78;cx.fillStyle=rgba(col,.88);rr(lp.x-bw/2,lp.y-bh/2,bw,bh,3);cx.fill();cx.fillStyle='#fff';cx.textAlign='center';cx.textBaseline='middle';cx.fillText(txt,lp.x,lp.y);cx.restore();}
      if(!dim){const cc=L.c,sp=SW/(cc.length+1);cc.forEach((cn,ci)=>{const nx=-hw+sp*(ci+1),nz=(ci%2?1:-1)*18;const np=proj(nx,y+hh+1,nz);if(!np.ok)return;const r=Math.max(.4,3*np.s),pu=1+Math.sin(t*1.5+ci+i)*.15,gr=Math.max(.5,r*2.5*pu),cr=Math.max(.3,r*pu);const g=cx.createRadialGradient(np.x,np.y,0,np.x,np.y,gr);g.addColorStop(0,rgba(col,.2));g.addColorStop(1,rgba(col,0));cx.fillStyle=g;cx.beginPath();cx.arc(np.x,np.y,gr,0,Math.PI*2);cx.fill();cx.fillStyle=col;cx.beginPath();cx.arc(np.x,np.y,cr,0,Math.PI*2);cx.fill();if((on||hi)&&cc.length<=5){const nf=Math.max(6.5,7.5*np.s);cx.font=`500 ${nf}px monospace`;cx.textAlign='center';const nw=cx.measureText(cn).width+6,nh=nf+3;cx.fillStyle='rgba(8,13,28,.85)';rr(np.x-nw/2,np.y-cr*3-nh,nw,nh,2);cx.fill();cx.fillStyle='rgba(255,255,255,.85)';cx.textBaseline='middle';cx.fillText(cn,np.x,np.y-cr*3-nh/2);}});}
      let minX=Infinity,maxX=-Infinity,minY=Infinity,maxY=-Infinity;
      p.forEach(pt=>{if(pt.x<minX)minX=pt.x;if(pt.x>maxX)maxX=pt.x;if(pt.y<minY)minY=pt.y;if(pt.y>maxY)maxY=pt.y;});
      hits.push({i,x1:minX,x2:maxX,y1:minY,y2:maxY,z:p[0].z});
    }
    function grid(){const gy=BY-25,gs=260,st=40;cx.strokeStyle='rgba(37,99,235,.03)';cx.lineWidth=.5;for(let x=-gs;x<=gs;x+=st){const a=proj(x,gy,-gs),b=proj(x,gy,gs);cx.beginPath();cx.moveTo(a.x,a.y);cx.lineTo(b.x,b.y);cx.stroke();}for(let z=-gs;z<=gs;z+=st){const a=proj(-gs,gy,z),b=proj(gs,gy,z);cx.beginPath();cx.moveTo(a.x,a.y);cx.lineTo(b.x,b.y);cx.stroke();}}
    function ambient(){ap.forEach(p=>{p.y+=p.vy;if(p.y>220){p.y=-220;p.x=(Math.random()-.5)*500;}const pp=proj(p.x,p.y,p.z);if(!pp.ok)return;const r=Math.max(.1,p.r*pp.s);cx.fillStyle=`rgba(37,99,235,${p.a*.2})`;cx.beginPath();cx.arc(pp.x,pp.y,r,0,Math.PI*2);cx.fill();});}
    function flows(){cp2.forEach(f=>{f.t=(f.t+f.s)%1;const fy=BY+f.a*GAP,ty=BY+f.b*GAP,cy2=fy+(ty-fy)*f.t,px=f.xo+Math.sin(f.t*Math.PI)*15,pz=f.zo+Math.cos(f.t*Math.PI)*8;const pp=proj(px,cy2,pz);if(!pp.ok)return;const col=f.t<.5?LAYERS[f.a].col:LAYERS[f.b].col;const or=Math.max(.5,2.5*pp.s),ir=Math.max(.2,.9*pp.s);const g=cx.createRadialGradient(pp.x,pp.y,0,pp.x,pp.y,or);g.addColorStop(0,rgba(col,.25));g.addColorStop(1,rgba(col,0));cx.fillStyle=g;cx.beginPath();cx.arc(pp.x,pp.y,or,0,Math.PI*2);cx.fill();cx.fillStyle=col;cx.globalAlpha=.4;cx.beginPath();cx.arc(pp.x,pp.y,ir,0,Math.PI*2);cx.fill();cx.globalAlpha=1;});}

    let rafId: number;
    const t0=performance.now();
    function render(){
      rafId=requestAnimationFrame(render);
      const t=(performance.now()-t0)/1000;
      if(auto&&t>2)tT+=.0006;
      cT+=(tT-cT)*.04;cP+=(tP-cP)*.04;cD+=(tD-cD)*.04;
      cx.clearRect(0,0,W,H);
      const bg=cx.createRadialGradient(W/2,H/2,0,W/2,H/2,Math.max(W,H)*.7);
      bg.addColorStop(0,'#131C2E');bg.addColorStop(1,'#0F172A');cx.fillStyle=bg;cx.fillRect(0,0,W,H);
      grid();ambient();flows();
      const order=LAYERS.map((_,i)=>({i,d:proj(0,BY+i*GAP,0).z})).sort((a,b)=>b.d-a.d);
      hits=[];order.forEach(({i})=>slab(i,t));
    }
    render();

    let drag=false,moved=false,lx=0,ly=0,atm: ReturnType<typeof setTimeout>;
    function onDown(e:PointerEvent){e.preventDefault();drag=true;moved=false;auto=false;lx=e.clientX;ly=e.clientY;cv.style.cursor='grabbing';}
    function onMove(e:PointerEvent){if(!drag)return;const dx=e.clientX-lx,dy=e.clientY-ly;if(Math.abs(dx)>3||Math.abs(dy)>3)moved=true;if(moved){tT-=dx*.004;tP=Math.max(.25,Math.min(1.15,tP+dy*.004));}lx=e.clientX;ly=e.clientY;}
    function onUp(e:PointerEvent){if(!drag)return;drag=false;cv.style.cursor='grab';clearTimeout(atm);atm=setTimeout(()=>auto=true,6000);if(!moved){const rc=cv.getBoundingClientRect(),mx=e.clientX-rc.left,my=e.clientY-rc.top;let bestI=-1,bestDist=Infinity;for(let j=0;j<hits.length;j++){const h=hits[j];if(mx>=h.x1&&mx<=h.x2&&my>=h.y1&&my<=h.y2){const cy=(h.y1+h.y2)/2,dist=Math.abs(my-cy);if(dist<bestDist){bestDist=dist;bestI=h.i;}}}if(bestI>=0){selLyr(bestI);}else{desel();}}}
    function onWheel(e:WheelEvent){e.preventDefault();tD=Math.max(340,Math.min(650,tD+e.deltaY*.3));}
    function onHover(e:MouseEvent){if(drag)return;const rc=cv.getBoundingClientRect(),mx=e.clientX-rc.left,my=e.clientY-rc.top;let over=false;for(let j=hits.length-1;j>=0;j--){const h=hits[j];if(mx>=h.x1&&mx<=h.x2&&my>=h.y1&&my<=h.y2){over=true;break;}}cv.style.cursor=over?'pointer':'grab';}

    function selLyr(i:number){
      hl=null;if(flsRef.current)flsRef.current.querySelectorAll('.fl3d').forEach((e:Element)=>e.classList.remove('on'));
      if(sel===i){desel();return;}sel=i;
      const L=LAYERS[i];
      if(dcBRef.current){dcBRef.current.textContent=`LAYER ${L.id}`;dcBRef.current.style.cssText=`background:${L.col}22;color:${L.col}`;}
      if(dcTRef.current)dcTRef.current.textContent=L.nm;
      if(dcDRef.current)dcDRef.current.textContent=L.desc;
      if(dcCRef.current)dcCRef.current.innerHTML=L.c.map(c=>`<span class="dc3d-chip">${c}</span>`).join('')+L.l.map(l=>`<span class="dc3d-chip lib">${l}</span>`).join('');
      if(dcPRef.current)dcPRef.current.innerHTML=`<strong>Depends on:</strong> ${L.deps}<br/><strong>Consumed by:</strong> ${L.used}`;
      if(dcRef.current)dcRef.current.classList.add('show');
    }
    function desel(){sel=-1;if(dcRef.current)dcRef.current.classList.remove('show');}

    // Build flow items
    const flsEl=flsRef.current;
    if(flsEl){
      flsEl.innerHTML='';
      FLOWS.forEach(f=>{
        const el=document.createElement('div');el.className='fl3d';
        const tags=f.layers.map(id=>{const L=LAYERS[id-1];return`<span class="fl3d-t" style="background:${L.col}22;color:${L.col}">L${id}</span>`;}).join('');
        el.innerHTML=`<div class="fl3d-ic" style="background:${f.col}15;color:${f.col}">${f.ic}</div><div><div class="fl3d-nm" style="color:${f.col}">${f.nm}</div><div class="fl3d-tags">${tags}</div></div>`;
        el.addEventListener('mouseenter',()=>{hl=f.layers;sel=-1;if(dcRef.current)dcRef.current.classList.remove('show');if(flsEl)flsEl.querySelectorAll('.fl3d').forEach((e:Element)=>e.classList.remove('on'));el.classList.add('on');});
        el.addEventListener('mouseleave',()=>{hl=null;el.classList.remove('on');});
        el.addEventListener('click',(e)=>{e.stopPropagation();if(hl===f.layers&&el.classList.contains('on')){hl=null;el.classList.remove('on');}else{hl=f.layers;sel=-1;if(dcRef.current)dcRef.current.classList.remove('show');if(flsEl)flsEl.querySelectorAll('.fl3d').forEach((e:Element)=>e.classList.remove('on'));el.classList.add('on');}});
        flsEl.appendChild(el);
      });
    }

    const dcXEl=dcXRef.current;
    if(dcXEl)dcXEl.addEventListener('click',(e)=>{e.stopPropagation();desel();});

    cv.addEventListener('pointerdown',onDown);
    window.addEventListener('pointermove',onMove);
    window.addEventListener('pointerup',onUp);
    cv.addEventListener('wheel',onWheel,{passive:false});
    cv.addEventListener('mousemove',onHover);
    window.addEventListener('resize',resize);

    return()=>{
      cancelAnimationFrame(rafId);
      cv.removeEventListener('pointerdown',onDown);
      window.removeEventListener('pointermove',onMove);
      window.removeEventListener('pointerup',onUp);
      cv.removeEventListener('wheel',onWheel);
      cv.removeEventListener('mousemove',onHover);
      window.removeEventListener('resize',resize);
      clearTimeout(atm);
    };
  }, []);

  const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };
      const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        
        // Validate email
        if (!validateEmail(formData.email)) {
          setSubmitMessage('Please enter a valid email address');
          return;
        }
    
        // Validate age confirmation
        if (!formData.ageConfirm) {
          setSubmitMessage('Please confirm you are 18 or older');
          return;
        }
        setIsSubmitting(true);
        setSubmitMessage('');
        try {
          const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              user_type: formData.type
            })
          });
    
          const raw = await response.json();
          let data: any;
          if (raw.body && typeof raw.body === 'string') {
            try { data = JSON.parse(raw.body); } catch { data = raw; }
          } else { data = raw; }
          const statusCode = raw.statusCode || response.status;
          if (statusCode === 200 && !data.error) {
            if (data.new_subscription) {
              setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
            } else {
              setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
            }
            setFormData({ email: '', type: 'merchant', ageConfirm: false });
            setTimeout(() => {
              setModalOpen(false);
              setSubmitMessage('');
            }, 3000);
          } else {
            setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
          }
        } catch (error) {
          console.error('Error submitting email:', error);
          setSubmitMessage('An error occurred. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error message when user starts typing
        if (submitMessage) setSubmitMessage('');
      };

  return (
    <div className="min-h-screen bg-white text-gray-900">
     <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');

        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--blue-600);
          color: var(--white);
        }
        .btn-primary:hover {
          background: var(--blue-700);
          transform: translateY(-1px);
        }

        .btn-outline {
          background: transparent;
          color: var(--gray-900);
          border: 1px solid var(--gray-200);
        }
        .btn-outline:hover {
          border-color: var(--blue-600);
          color: var(--blue-600);
        }

        .btn-gold {
          background: var(--gold);
          color: var(--gray-900);
        }
        .btn-gold:hover {
          background: var(--gold-dark);
          transform: translateY(-1px);
        }

        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }

        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .modal-field {
          margin-bottom: 20px;
        }

        .modal-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--gray-500);
          margin-bottom: 8px;
        }

        .modal-field input,
        .modal-field select {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .modal-field input:focus,
        .modal-field select:focus {
          outline: none;
          border-color: var(--blue-600);
        }

        .modal-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 24px;
        }

        .modal-checkbox input {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: var(--blue-600);
        }

        .modal-checkbox span {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.5;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
        }

        .modal-buttons .btn {
          flex: 1;
          justify-content: center;
          padding: 14px 20px;
        }

        .hero {
          padding: 140px 0 80px;
          background: linear-gradient(135deg, var(--off-white) 0%, var(--white) 100%);
        }

        .hero-content {
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
          background: linear-gradient(135deg, var(--gray-900) 0%, var(--blue-600) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero p {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: var(--gray-500);
          line-height: 1.6;
          margin-bottom: 32px;
          letter-spacing: 0.3px;
        }

        section {
          padding: 56px 0;
        }

        .section-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 700;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 18px;
          color: var(--gray-500);
          max-width: 600px;
          margin: 0 auto;
        }

        .section-alt { background: var(--off-white); }
        .section-dark { background: var(--gray-900); color: var(--white); }

        .section-label {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }
        .section-dark .section-label { background: rgba(212, 168, 83, 0.15); color: var(--gold); }

        .hero-label {
          display: inline-block;
          background: var(--white);
          border: 1px solid var(--gray-200);
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: var(--gray-500);
          margin-bottom: 24px;
        }

        /* 3D Architecture Viewport */
        .vp3d{position:relative;width:100%;height:640px;background:#0F172A;border-radius:16px;overflow:hidden;margin-top:32px}
        .vp3d canvas{display:block;width:100%;height:100%;touch-action:none;cursor:grab}
        .dc3d{position:absolute;top:16px;left:16px;bottom:16px;z-index:10;width:300px;background:rgba(8,13,28,.94);border:1px solid rgba(37,99,235,.15);border-radius:12px;padding:20px;backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);color:#fff;display:flex;flex-direction:column;justify-content:center;opacity:0;transform:translateX(-12px);transition:all .3s cubic-bezier(.16,1,.3,1);pointer-events:none}
        .dc3d.show{opacity:1;transform:translateX(0);pointer-events:auto}
        .dc3d-x{position:absolute;top:12px;right:14px;background:none;border:none;color:#64748B;cursor:pointer;font-size:18px;line-height:1;padding:4px;transition:color .15s}
        .dc3d-x:hover{color:#fff}
        .dc3d-badge{display:inline-block;font-size:9px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;padding:3px 9px;border-radius:4px;margin-bottom:8px;align-self:flex-start}
        .dc3d-title{font-size:20px;font-weight:700;margin-bottom:6px}
        .dc3d-desc{font-size:12.5px;color:#94A3B8;line-height:1.55;margin-bottom:14px}
        .dc3d-chips{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:12px}
        .dc3d-chip{font-size:10px;padding:3px 8px;border-radius:5px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);color:rgba(255,255,255,.82);white-space:nowrap}
        .dc3d-chip.lib{border-color:rgba(212,168,83,.3);color:#D4A853}
        .dc3d-deps{font-size:11px;color:#64748B;line-height:1.5}
        .dc3d-deps strong{color:#2563EB;font-weight:600}
        .fp3d{position:absolute;top:16px;right:16px;z-index:10;width:256px;background:rgba(8,13,28,.94);border:1px solid rgba(37,99,235,.15);border-radius:12px;padding:14px 14px 10px;backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);color:#fff}
        .fp3d-title{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#D4A853;margin-bottom:10px}
        .fl3d{display:flex;gap:8px;align-items:flex-start;padding:7px 6px;margin:0 -6px;border-radius:6px;cursor:pointer;transition:background .15s;border-bottom:1px solid rgba(255,255,255,.03)}
        .fl3d:last-child{border-bottom:none}
        .fl3d:hover{background:rgba(255,255,255,.05)}
        .fl3d.on{background:rgba(37,99,235,.12)}
        .fl3d-ic{width:20px;height:20px;flex-shrink:0;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:9px;margin-top:2px}
        .fl3d-nm{font-size:11px;font-weight:600;margin-bottom:1px}
        .fl3d-tags{display:flex;gap:2px;margin-top:3px;flex-wrap:wrap}
        .fl3d-t{font-size:7.5px;font-weight:600;padding:1px 4px;border-radius:2px;letter-spacing:.3px}
        @media(max-width:768px){.vp3d{height:480px}.fp3d{position:relative;top:auto;right:auto;width:100%;border-radius:0 0 12px 12px}.dc3d{width:calc(100% - 32px);left:16px;top:auto;bottom:16px;height:auto;transform:translateY(8px)}.dc3d.show{transform:translateY(0)}.vp3d{border-radius:16px 16px 0 0}}

        .engine-box {
          background: linear-gradient(135deg, #1E3A5F 0%, #2d4a6f 100%);
          border-radius: 16px;
          padding: 40px;
          color: var(--white);
        }

        .engine-header { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }

        .engine-icon {
          width: 56px;
          height: 56px;
          background: var(--gold);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .engine-icon svg { width: 28px; height: 28px; stroke: var(--gray-900); }

        .engine-title { font-size: 24px; font-weight: 700; }
        .engine-subtitle { font-size: 14px; color: rgba(255,255,255,0.7); }

        .engine-desc { color: rgba(255,255,255,0.8); margin-bottom: 24px; line-height: 1.7; }

        .engine-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .engine-feature {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .engine-feature-icon {
          width: 28px;
          height: 28px;
          background: var(--gold);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .engine-feature-icon svg { width: 14px; height: 14px; stroke: var(--gray-900); stroke-width: 2.5; }

        .engine-feature-text { font-size: 14px; color: rgba(255,255,255,0.9); }

        .app-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }

        .app-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 28px;
          transition: all 0.2s;
        }

        .app-card:hover {
          border-color: var(--blue-600);
          box-shadow: 0 8px 24px rgba(0,0,0,0.06);
        }

        .app-icon {
          width: 48px;
          height: 48px;
          background: var(--gray-100);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .app-icon svg { width: 24px; height: 24px; stroke: var(--blue-600); }

        .app-title { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
        .app-desc { color: var(--gray-500); font-size: 14px; margin-bottom: 16px; }

        .app-features { list-style: none; }
        .app-features li {
          padding: 5px 0;
          color: var(--gray-500);
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .check-sm {
          width: 16px;
          height: 16px;
          background: var(--green-600);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .check-sm svg { width: 8px; height: 8px; stroke: white; stroke-width: 3; }

        .flow-section { padding: 48px 0; }

        .flow-label {
          font-size: 14px;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 16px;
          text-align: center;
        }

        .flow-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          padding: 24px;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          margin-bottom: 32px;
        }

        .flow-step {
          background: var(--off-white);
          border: 2px solid var(--gray-200);
          border-radius: 10px;
          padding: 16px 20px;
          text-align: center;
          min-width: 100px;
          transition: all 0.2s;
        }

        .flow-step:hover { border-color: var(--blue-600); }

        .flow-step-icon {
          width: 36px;
          height: 36px;
          margin: 0 auto 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .flow-step-icon svg { width: 24px; height: 24px; stroke: var(--blue-600); }

        .flow-step h4 { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
        .flow-step span { font-size: 11px; color: var(--gray-500); }

        .flow-arrow { color: var(--gold); font-size: 18px; font-weight: bold; }

        .region-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .region-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 24px 16px;
          text-align: center;
        }

        .region-name { font-size: 16px; font-weight: 700; margin-bottom: 8px; }

        .region-multiplier {
          display: inline-block;
          background: var(--gold);
          color: var(--gray-900);
          padding: 4px 16px;
          border-radius: 20px;
          font-weight: 700;
          font-size: 14px;
        }

        .region-desc { font-size: 12px; color: rgba(255,255,255,0.6); margin-top: 8px; }

        .integration-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .integration-card {
          background: var(--off-white);
          border-radius: 12px;
          padding: 24px;
          border-left: 4px solid var(--blue-600);
        }

        .integration-title {
          font-size: 15px;
          font-weight: 700;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .integration-title svg { width: 20px; height: 20px; stroke: var(--blue-600); }

        .integration-desc { font-size: 13px; color: var(--gray-500); }

        .cta-section {
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          padding: 80px 0;
          text-align: center;
          color: var(--white);
        }

        .cta-section h2 {
          font-size: 40px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .cta-section p {
          font-size: 18px;
          margin-bottom: 32px;
          opacity: 0.9;
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero p { font-size: 16px; }

          .app-grid { grid-template-columns: 1fr; }
          .integration-grid { grid-template-columns: 1fr; }
          .region-grid { grid-template-columns: repeat(2, 1fr); }
          .engine-features { grid-template-columns: 1fr; }

          .promo-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .promo-stats { flex-wrap: wrap; }

          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }

          .modal-buttons { flex-direction: column; }
        }
      `}</style>

      {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Header onJoinWaitlist={() => setModalOpen(true)} />
   

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-label">Technical Documentation · Patent Pending</div>
            <h1>Platform Architecture</h1>
            <p>The infrastructure behind earn-time commitment. Seven-layer smart contract system on Polygon — 38+ smart contracts, AI receipt validation, dual-adapter bridge, and patent-pending earn-time token commitment architecture. Built for security, scale, and trust.</p>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="arch-section" style={{padding:'56px 0',background:'#fff'}}>
        <div style={{maxWidth:'1140px',margin:'0 auto',padding:'0 24px'}}>
          <div style={{textAlign:'center',marginBottom:'32px'}}>
            <div style={{display:'inline-block',background:'#EFF6FF',color:'#1D4ED8',fontSize:'13px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',padding:'6px 18px',borderRadius:'20px',marginBottom:'16px'}}>System Architecture</div>
            <h2 style={{fontSize:'clamp(28px,4vw,40px)',fontWeight:800,color:'#0F172A',margin:'0 0 12px'}}>Seven-Layer Smart Contract Stack</h2>
            <p style={{fontSize:'17px',color:'#64748B',maxWidth:'620px',margin:'0 auto'}}>38+ smart contracts across 7 layers. Drag to rotate · Scroll to zoom · Click a slab for details.</p>
          </div>
          <div className="vp3d" ref={vpRef}>
            <canvas ref={canvasRef} style={{display:'block',width:'100%',height:'100%',touchAction:'none',cursor:'grab'}} />
            <div className="dc3d" ref={dcRef}>
              <button className="dc3d-x" ref={dcXRef}>×</button>
              <div className="dc3d-badge" ref={dcBRef}></div>
              <div className="dc3d-title" ref={dcTRef}></div>
              <div className="dc3d-desc" ref={dcDRef}></div>
              <div className="dc3d-chips" ref={dcCRef}></div>
              <div className="dc3d-deps" ref={dcPRef}></div>
            </div>
            <div className="fp3d">
              <div className="fp3d-title">Coalition Trust Flows</div>
              <div ref={flsRef}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Promotion Engine */}
      <section style={{padding:'72px 0',background:'#F8FAFC'}}>
        <div style={{maxWidth:'1140px',margin:'0 auto',padding:'0 24px'}}>
          <div style={{textAlign:'center',marginBottom:'48px'}}>
            <div className="section-label" style={{display:'inline-block',marginBottom:'16px'}}>Promotion Engine</div>
            <h2 className="section-title">Smart Promotion Engine</h2>
            <p className="section-subtitle">150+ pre-built templates across 10 industries. Describe your goal in plain language — the AI generates a complete campaign configuration in under 2 minutes.</p>
          </div>

          <div className="promo-stats" style={{display:'flex',justifyContent:'center',gap:'20px',marginBottom:'48px'}}>
            {[['150+','Promotion Templates'],['10','Industry Verticals']].map(([v,l])=>(
              <div key={l} style={{background:'#fff',borderRadius:'12px',padding:'24px 48px',textAlign:'center',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                <div style={{fontSize:'32px',fontWeight:800,color:'#1D4ED8',marginBottom:'4px'}}>{v}</div>
                <div style={{fontSize:'13px',color:'#64748B',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>{l}</div>
              </div>
            ))}
          </div>

          <div className="promo-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'16px',marginBottom:'40px'}}>
            {[
              {tag:'Mall & Retail',name:'Shop 3, Earn 1000',desc:'Visit Food + Fashion + Cinema in one trip. Cross-merchant bundle rewards drive foot traffic across your entire ecosystem.'},
              {tag:'Healthcare',name:'Wellness Check Bonus',desc:'Patients earn +100 points for completing annual preventive care. Reward outcomes, not just transactions.'},
              {tag:'Airlines',name:'Status Match 2x',desc:'Matched-tier members earn double miles for 90 days. Convert competitive loyalty into yours.'},
              {tag:'Employee Programs',name:'Peer Recognition',desc:'Employees award +50 points to colleagues. Recognition flows peer-to-peer, not just top-down.'},
              {tag:'Hotels',name:'Direct Booking Bonus',desc:'+500 points when guests book direct. Reward the relationship, not the aggregator.'},
              {tag:'Sustainability',name:'Green Purchase Bonus',desc:'+50 points for eco-friendly products. Reward the choices that matter.'},
              {tag:'Government',name:'Transit Rider Bonus',desc:'+10 points per ride. Reward the civic behavior you want to see. Points never expire.'},
              {tag:'Non-Profit',name:'Donation Match 2x',desc:'Every donation matched with 2x loyalty tokens. Donors see their impact multiply on-chain.'}
            ].map(({tag,name,desc})=>(
              <div key={name} style={{background:'#fff',borderRadius:'12px',padding:'20px',borderLeft:'4px solid #D4A853',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                <div style={{fontSize:'11px',fontWeight:700,color:'#D4A853',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'8px'}}>{tag}</div>
                <div style={{fontSize:'15px',fontWeight:700,color:'#0F172A',marginBottom:'6px'}}>{name}</div>
                <div style={{fontSize:'13px',color:'#64748B',lineHeight:1.5}}>{desc}</div>
              </div>
            ))}
          </div>

          <div style={{textAlign:'center',fontSize:'12px',fontWeight:600,color:'#94A3B8',letterSpacing:'0.08em',textTransform:'uppercase',marginTop:'8px'}}>
            Airlines &nbsp;·&nbsp; Hotels &nbsp;·&nbsp; Mall &amp; Retail &nbsp;·&nbsp; Employee Programs &nbsp;·&nbsp; Healthcare &nbsp;·&nbsp; Non-Profit &nbsp;·&nbsp; Sustainability &nbsp;·&nbsp; Conglomerate &nbsp;·&nbsp; Fintech &amp; Banking &nbsp;·&nbsp; Government
          </div>
        </div>
      </section>

    <section style={{background:'#0F172A',padding:'80px 0'}}>
        <div className="container">
            <div className="section-header">
                <div className="section-label">Core Technology</div>
                <h2 className="section-title" style={{color:'#fff'}}>Intelligent Liquidity Engine</h2>
                <p className="section-subtitle" style={{color:'rgba(255,255,255,0.6)'}}>Oracle-integrated token management for sustainable economics.</p>
            </div>

            <div className="engine-box">
                <div className="engine-header">
                    <div className="engine-icon">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/></svg>
                    </div>
                    <div>
                        <div className="engine-title">Smart Liquidity Management</div>
                        <div className="engine-subtitle">Powered by Chainlink Oracles</div>
                    </div>
                </div>
                <p className="engine-desc">
                    The RWLY token engine uses real-time oracle data to optimize liquidity, prevent manipulation, and maintain stable conversion rates across all four global regions.
                </p>
                <div className="engine-features">
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/></svg></div>
                        <div className="engine-feature-text">Real-time oracle price feeds via Chainlink integration</div>
                    </div>
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/></svg></div>
                        <div className="engine-feature-text">Anti-manipulation safeguards with slippage protection</div>
                    </div>
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.97zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.97z"/></svg></div>
                        <div className="engine-feature-text">Dynamic fee adjustment based on market conditions</div>
                    </div>
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"/></svg></div>
                        <div className="engine-feature-text">Regional multiplier optimization for equitable access</div>
                    </div>
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/></svg></div>
                        <div className="engine-feature-text">Automated rebalancing across liquidity pools</div>
                    </div>
                    <div className="engine-feature">
                        <div className="engine-feature-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/></svg></div>
                        <div className="engine-feature-text">Predictive analytics for demand forecasting</div>
                    </div>
                </div>
            </div>
        </div>
    </section>
      {/* Applications */}
      <section style={{background: 'var(--off-white)'}}>
        <div className="container">
          <div className="section-header">
            <div className="section-label">Client Applications</div>
            <h2 className="section-title">Two Portals, One Ecosystem</h2>
            <p className="section-subtitle">Purpose-built for consumers and the organizations that serve them.</p>
          </div>

          <div className="app-grid">
            {/* Card 1: Consumer App */}
            <div className="app-card">
              <div className="app-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"/></svg>
              </div>
              <div className="app-title">Consumer App</div>
              <p className="app-desc">Your Rewards, Your Way</p>
              <ul className="app-features">
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Earn rewards from any program — employers, merchants, transit, healthcare, community organizations</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Choose how to use them: gift cards, bank transfer, Rewardly Card, merchant spend, donate, or hold</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Earn interest on rewards you hold — lock for 1 to 24 months for higher returns</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Transfer value across borders — rewards work in any region in the network</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Points from different programs combine into one balance</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Your rewards never expire. Once earned, they belong to you.</li>
              </ul>
            </div>

            {/* Card 2: Coalition Portal */}
            <div className="app-card">
              <div className="app-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/></svg>
              </div>
              <div className="app-title">Coalition Portal</div>
              <p className="app-desc">Launch, Manage, Grow</p>

              <div style={{fontSize:'11px',fontWeight:700,color:'var(--gold, #D4A853)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px',marginTop:'4px'}}>For Anchors &amp; Enterprises</div>
              <ul className="app-features">
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Launch branded loyalty programs with 150+ promotion templates</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Fund and manage your RWLY reward treasury</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Real-time analytics: redemption rates, consumer engagement, program ROI</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>AI-powered compliance scoring and promotion optimization</li>
              </ul>

              <div style={{borderTop:'1px solid var(--gray-200)',margin:'16px 0'}}></div>

              <div style={{fontSize:'11px',fontWeight:700,color:'var(--gold, #D4A853)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>For Merchants &amp; Partners</div>
              <ul className="app-features">
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Join any coalition — start earning and settling in days</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Run promotional campaigns to drive foot traffic</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>View customer insights and earning activity in real time</li>
                <li><span className="check-sm"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>No POS integration required — works alongside existing systems</li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      <section className="section-alt flow-section">
        <div className="container">
            <div className="section-header">
                <div className="section-label">Core Flows</div>
                <h2 className="section-title">How Value Moves</h2>
                <p className="section-subtitle">Simplified flows showing the journey from points to purchasing power.</p>
            </div>

            <div className="flow-label">Enterprise Onboarding</div>
            <div className="flow-container">
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/></svg></div>
                    <h4>Enterprise</h4>
                    <span>Airline, hotel, retail</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/></svg></div>
                    <h4>API Integration</h4>
                    <span>Connect systems</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/></svg></div>
                    <h4>Escrow Setup</h4>
                    <span>Secure allocation</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg></div>
                    <h4>Point Sync</h4>
                    <span>Import balances</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/></svg></div>
                    <h4>RWLY Minted</h4>
                    <span>Users receive tokens</span>
                </div>
            </div>

            <div className="flow-label">Points → RWLY Conversion</div>
            <div className="flow-container">
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/></svg></div>
                    <h4>Loyalty Points</h4>
                    <span>Airline, hotel, retail</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/></svg></div>
                    <h4>Oracle Pricing</h4>
                    <span>Real-time rates</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"/></svg></div>
                    <h4>Region Bonus</h4>
                    <span>+30% to +60% more</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/></svg></div>
                    <h4>Fee Applied</h4>
                    <span>0.5 – 1.5%</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/></svg></div>
                    <h4>RWLY Tokens</h4>
                    <span>In your wallet</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg></div>
                    <h4>Redeem</h4>
                    <span>Gift Cards · Bank Transfer · Rewardly Card · Hold &amp; Spend</span>
                </div>
            </div>

            <div className="flow-label">Merchant Payment</div>
            <div className="flow-container">
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg></div>
                    <h4>Customer</h4>
                    <span>Opens app</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"/><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"/></svg></div>
                    <h4>Scan QR</h4>
                    <span>Or tap NFC</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/></svg></div>
                    <h4>Smart Contract</h4>
                    <span>Instant settlement</span>
                </div>
                <span className="flow-arrow">→</span>
                <div className="flow-step">
                    <div className="flow-step-icon"><svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"/></svg></div>
                    <h4>Merchant</h4>
                    <span>Receives RWLY</span>
                </div>
            </div>
        </div>
    </section>

    <section className="section-dark">
        <div className="container">
            <div className="section-header">
                <div className="section-label">Global Coverage</div>
                <h2 className="section-title">Regional Infrastructure</h2>
                <p className="section-subtitle">Four global regions with bonus multipliers. A 1.5x multiplier means users receive 50% more RWLY tokens when converting points — designed to encourage adoption in emerging markets.</p>
            </div>

            <div className="region-grid">
                <div className="region-card">
                    <div className="region-name">APAC</div>
                    <div className="region-multiplier">1.5x Bonus</div>
                    <div className="region-desc">50% more RWLY per point converted</div>
                </div>
                <div className="region-card">
                    <div className="region-name">Europe</div>
                    <div className="region-multiplier">1.3x Bonus</div>
                    <div className="region-desc">30% more RWLY per point converted</div>
                </div>
                <div className="region-card">
                    <div className="region-name">Americas</div>
                    <div className="region-multiplier">1.4x Bonus</div>
                    <div className="region-desc">40% more RWLY per point converted</div>
                </div>
                <div className="region-card">
                    <div className="region-name">MEA</div>
                    <div className="region-multiplier">1.6x Bonus</div>
                    <div className="region-desc">60% more RWLY per point converted</div>
                </div>
            </div>
        </div>
    </section>

    
    <section>
        <div className="container">
            <div className="section-header">
                <div className="section-label">Enterprise Integration</div>
                <h2 className="section-title">Integration Layer</h2>
                <p className="section-subtitle">Enterprise-ready APIs and connectors for seamless integration.</p>
            </div>

            <div className="integration-grid">
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/></svg>
                        REST API
                    </div>
                    <div className="integration-desc">Full-featured API for wallet operations, transactions, and analytics.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>
                        5-Path Conversion
                    </div>
                    <div className="integration-desc">Gift cards, bank transfers, Rewardly Card, merchant credit, or direct RWLY — five ways to redeem. All paths honor the earn-time committed value.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"/></svg>
                        Cross-Chain Bridge
                    </div>
                    <div className="integration-desc">Dual-adapter bridge: LayerZero for EVM chains, Wormhole NTT for Solana. Pool health gating protects all operations.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/></svg>
                        Webhooks
                    </div>
                    <div className="integration-desc">Real-time event notifications with retry logic.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"/></svg>
                        Keycloak SSO
                    </div>
                    <div className="integration-desc">Enterprise single sign-on with SAML and OAuth2.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/></svg>
                        Web3Auth
                    </div>
                    <div className="integration-desc">Non-custodial key management without seed phrases.</div>
                </div>
                <div className="integration-card">
                    <div className="integration-title">
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/></svg>
                        Market Operations
                    </div>
                    <div className="integration-desc">Internal crossing engine matches conversions before hitting DEX. Hot-swappable executors minimize slippage.</div>
                </div>
            </div>
        </div>
    </section>
      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Get Early Access</h2>
          <p>Be among the first to unlock your loyalty points.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Get Early Access →</button>
            <a href="/whitepaper" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Read the Whitepaper →</a>
          </div>
        </div>
      </section>

      {/* Footer */}
     <Footer />
    </div>
  );
};

export default PlatformPage;